import React, { useEffect, useMemo, useRef, useState } from "react"
import { useAppSelector } from "integrations/redux/hooks"
import { selectPanelModeByName } from "panels/state/selectors"
import { PanelModes, PanelNames } from "panels/constants"
import { default as HotspotsLayer } from "./layers/hotspots/HotspotsLayer.class"
import { default as FlightsLayer } from "./layers/flights/FlightsLayer.class"
import { LayerId } from "./layers/constants"
import {
    selectActiveImageIds,
    selectHoveredImageID,
    selectPreviewLayerByIdMapForFlightPath,
    selectPreviewLayerByIdMapForHotspotPoints,
    selectPreviewLayerByIdMapForHotspotPolygons,
} from "./layers/selectors"
import { layerSpecificTooltipGenerator } from "./layers/registry"
import { getMapLayerPlugins } from "plugins/plugin-registry"
import VideoLayer from "./layers/video/Video.class"
import {
    selectAllFlightsMap,
    selectAllInfraredFlightsMap,
    selectFlightsWithHotspotLabels,
    selectFlightsWithHotspotPoints,
    selectFlightsWithHotspotPolygons,
    selectNonArchivedInfraredFlights,
    selectShowLayerByIdMapForFlightPaths,
} from "features/entity-caching/flights/selectors"
import {
    selectCrosshairEnabled,
    selectFrameOutlineEnabled,
    selectDataUpdatedTimestamp,
    VideoMetadata,
    selectFlightPathEnabled,
    selectDroneIconEnabled,
} from "@nova/slice-video"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { selectSelectedFrame } from "plugins/ColorFinder/ImageColorFinder/ImageColorFinderSlice"
import { useUniversalControl, videoData } from "plugins/shared/useUniversalControl"
import { selectActiveSidePanelModal, selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { selectActiveFirePerimeterPolygons } from "features/entity-caching/fire-perimeters/selectors"
import { selectHotspotPanelItems } from "panels/hotspot/selectors"
import { selectSelectedHotspotsMap } from "features/data-management/hotspots/selectors"
import {
    selectHotspotsSizeGraphByFlightId,
    selectHotspotsThermalGraphByFlightId,
    selectMapHotspots,
} from "features/entity-caching/hotspots/selectors"
import store from "integrations/redux/store"
import { selectCurrentGeometryToolType } from "plugins/shared/geometry/SharedGeometrySlice"
import { MapElements } from "./MapElements"
import { useBoundingBoxLayer } from "plugins/shared/layers/BoundingBoxLayer.class"
import { useWeatherLayers } from "features/map/layers/weather/useWeatherLayers"
import { selectActivatedFireId } from "./control/selectors"
import { useOverlays } from "./control/useOverlays"
import { getLastSnapshotTimestamp, takeMapSnapshot } from "./module"
import { useHeatLayer } from "./layers/overlays/useHeatLayer"
import { useFirePerimetersLayer } from "./layers/overlays/useFirePerimetersLayer"
import { useElevationLayer } from "./layers/overlays/useElevationLayer"
import { useCivilizationLayer } from "./layers/overlays/useCivilizationLayer"
import { LayerIndex, minZoomOnTerrain } from "@nova/constants"
import { TerrainLayer } from "@deck.gl/geo-layers"
import { config } from "@nova/configuration/browser"
import { useGlobalTerrainData } from "./GlobalTerrainManager"
import { getAncestors } from "panels/content/side-panel/utils"

const elevationDecoder = {
    rScaler: 6553.6,
    gScaler: 25.6,
    bScaler: 0.1,
    offset: -9990,
}

const parameters = { depthTest: false }

export default function MapLayerManager() {
    const apiKey = config.get("mapTilerApiKey")

    const [videoMetadata, setVideoMetadata] = useState<VideoMetadata>([])

    const { terrainState: terrainIsToggled, terrainMapIsLoaded } = useGlobalTerrainData([
        "terrainState",
        "terrainMapIsLoaded",
    ])

    const flight = useAppSelector(selectActiveSidePanelModal())
    const flightId: string | undefined = flight && flight.type === "layer" ? flight.id : ""
    const imageColorFinderSelectedFrame = useAppSelector(selectSelectedFrame())
    const mediaUpdatedTimestamp = useAppSelector(selectDataUpdatedTimestamp())

    // For the frame video layer.
    let videoLayerMediaType: "video" | "image"
    switch (flight?.layerContentType) {
        case "icf":
            videoLayerMediaType = "image"
            break
        default:
            videoLayerMediaType = "video"
    }

    useEffect(() => {
        setVideoMetadata([...videoData.metadata])
    }, [mediaUpdatedTimestamp])

    const hotspots = useAppSelector(selectMapHotspots)
    const allHotspots = useMemo(() => Object.values(hotspots), [hotspots])
    const infraredFlights = useAppSelector(selectNonArchivedInfraredFlights)
    const infraredFlightsMap = useAppSelector(selectAllInfraredFlightsMap)

    const activeFirePerimeterPolygons = useAppSelector(selectActiveFirePerimeterPolygons)
    const someGeometryToolIsInUse = !!useAppSelector(selectCurrentGeometryToolType)

    const showHotspotPointsLayerByFlightId = useAppSelector(selectFlightsWithHotspotPoints)
    const previewHotspotPointsLayerByFlightId = useAppSelector(
        selectPreviewLayerByIdMapForHotspotPoints
    )

    const showHotspotPolygonLayerByFlightId = useAppSelector(selectFlightsWithHotspotPolygons)

    const previewHotspotPolygonLayerByFlightId = useAppSelector(
        selectPreviewLayerByIdMapForHotspotPolygons
    )
    const showHotspotLabelsLayerByFlightId = useAppSelector(selectFlightsWithHotspotLabels)
    const showFlightPathsLayerByFlightId = useAppSelector(selectShowLayerByIdMapForFlightPaths)
    const previewFlightPathsLayerByFlightId = useAppSelector(selectPreviewLayerByIdMapForFlightPath)
    const allFlightsMap = useAppSelector(selectAllFlightsMap)
    const entityMap = useAppSelector(selectAllEntitiesMap)
    const activeFlightPaths = Object.keys({
        ...showFlightPathsLayerByFlightId,
        ...previewFlightPathsLayerByFlightId,
    }).filter((key) => {
        return (
            (showFlightPathsLayerByFlightId[key] || previewFlightPathsLayerByFlightId[key]) &&
            allFlightsMap[key] &&
            allFlightsMap[key].visible &&
            !getAncestors(key, entityMap).find((ancestor) => !ancestor.visible)
        )
    })
    const activeImageIds = useAppSelector(selectActiveImageIds)
    const hoveredImageId = useAppSelector(selectHoveredImageID)

    const snapshotPanelMode = useAppSelector(selectPanelModeByName(PanelNames.Snapshot))
    const snapshotPanelIsHidden = snapshotPanelMode === PanelModes.Hidden

    const crosshairEnabled = useAppSelector(selectCrosshairEnabled())
    const frameEnabled = useAppSelector(selectFrameOutlineEnabled())
    const flightPathEnabled = useAppSelector(selectFlightPathEnabled())
    const droneIconEnabled = useAppSelector(selectDroneIconEnabled())
    const { isLoading } = useUniversalControl()
    const pdfSnapshotOpen = useAppSelector(selectSnapshotOpen())
    const selectedHotspotsMap = useAppSelector(selectSelectedHotspotsMap)

    const boundingBoxLayer = useBoundingBoxLayer()
    const weatherLayers = useWeatherLayers("project")

    const overlays = useOverlays("project")
    const { heat, firePerimeters, elevation, civilization } = overlays
    const { layer: heatLayer } = useHeatLayer(heat.enabled)
    const firePerimetersLayer = useFirePerimetersLayer(firePerimeters.enabled)
    useElevationLayer(elevation.enabled)
    useCivilizationLayer(civilization.enabled)

    const projectId = useAppSelector(selectActivatedFireId)
    const anyOverlayOn =
        heat.enabled || firePerimeters.enabled || elevation.enabled || civilization.enabled
    const wasAnyOverlayOnRef = useRef(anyOverlayOn)
    const dirtySinceRef = useRef<number | null>(anyOverlayOn ? Date.now() : null)
    useEffect(() => {
        const wasOn = wasAnyOverlayOnRef.current
        if (anyOverlayOn && !wasOn) {
            dirtySinceRef.current = Date.now()
        } else if (!anyOverlayOn && wasOn) {
            const dirtySince = dirtySinceRef.current
            const lastSnapshot = projectId ? getLastSnapshotTimestamp(projectId) : undefined
            if (projectId && dirtySince && lastSnapshot && lastSnapshot >= dirtySince) {
                takeMapSnapshot(projectId)
            }
            dirtySinceRef.current = null
        }
        wasAnyOverlayOnRef.current = anyOverlayOn
    }, [anyOverlayOn, projectId])
    const plugins = getMapLayerPlugins()
    let firstPositiveIdx = Infinity
    const zIndexedLayers = plugins.flatMap((p) => {
        const layers = p.useLayer()
        if (Array.isArray(layers)) {
            return layers.map((layer) => {
                return { layer, zIndex: p.zIndex }
            })
        } else return { layer: layers, zIndex: p.zIndex }
    })
    if (boundingBoxLayer)
        zIndexedLayers.push({ layer: boundingBoxLayer, zIndex: LayerIndex.OrthoBBOX })
    if (heatLayer) zIndexedLayers.push({ layer: heatLayer, zIndex: LayerIndex.HeatOverlay })
    if (firePerimetersLayer)
        zIndexedLayers.push({
            layer: firePerimetersLayer,
            zIndex: LayerIndex.FirePerimetersOverlay,
        })
    if (!pdfSnapshotOpen) zIndexedLayers.push(...weatherLayers)
    zIndexedLayers.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
    zIndexedLayers.forEach((layer, idx) => {
        if (layer.zIndex && layer.zIndex >= 0) firstPositiveIdx = Math.min(firstPositiveIdx, idx)
    })
    const pluginLayers = zIndexedLayers.map((layer) => layer.layer)
    const activeHotspotPanelHotspotId = useAppSelector(selectHotspotPanelItems).hotspotId

    const [didComputeHotspotGraphs, setDidComputeHotspotGraphs] = useState(false)
    /**
     * Computes the hotspot graph data for all infrared flights by calling the selectors.
     * The selectors memoize per infrared flight ID and cache the graph computation. This
     * is needed so that the Hotspot HexagonLayer has data to display without first
     * requiring the hotspot inspector to have been opened.
     */
    useEffect(() => {
        if (didComputeHotspotGraphs) return

        const state = store.getState()
        infraredFlights.forEach((flight) => {
            selectHotspotsSizeGraphByFlightId(flight.id)(state)
            selectHotspotsThermalGraphByFlightId(flight.id)(state)
        })

        setDidComputeHotspotGraphs(true)
    }, [didComputeHotspotGraphs, infraredFlights])

    const layers = [
        new TerrainLayer({
            id: "terrain",
            minZoom: minZoomOnTerrain,
            maxZoom: 14,
            refinementStrategy: "no-overlap",
            elevationData: `https://api.maptiler.com/tiles/terrain-rgb-v2/{z}/{x}/{y}.webp?key=${apiKey}`,
            texture: null,
            wireframe: false,
            elevationDecoder,
            maxCacheSize: 100,
            visible: terrainIsToggled,
            operation: "terrain",
            loadOptions: {
                terrain: {
                    skirtHeight: 0,
                },
            },
            parameters,
        }),
        ...pluginLayers.slice(0, firstPositiveIdx),
        new HotspotsLayer({
            id: LayerId.Hotspots,
            pointId: LayerId.HotspotPoints,
            labelId: LayerId.HotspotLabels,
            data: allHotspots,
            pickable: snapshotPanelIsHidden && !someGeometryToolIsInUse,
            visible: true,
            updateTriggers: {
                getPosition: allHotspots,
                getFillColor: allHotspots,
                pointsLayerFilter: [
                    showHotspotPointsLayerByFlightId,
                    previewHotspotPointsLayerByFlightId,
                ],
                labelsLayerFilter: showHotspotLabelsLayerByFlightId,
                polygonLayerFilter: [
                    showHotspotPolygonLayerByFlightId,
                    previewHotspotPolygonLayerByFlightId,
                ],
            },
            flights: infraredFlightsMap,
            showLayer: {
                points: showHotspotPointsLayerByFlightId,
                labels: showHotspotLabelsLayerByFlightId,
                polygons: showHotspotPolygonLayerByFlightId,
            },
            previewLayer: {
                points: previewHotspotPointsLayerByFlightId,
                polygons: previewHotspotPolygonLayerByFlightId,
            },
            perimeterData: activeFirePerimeterPolygons || undefined,
            pdfSnapshotOpen,
            activeHotspotPanelHotspotId,
            selectedHotspotsMap,
        }),
        ...pluginLayers.slice(firstPositiveIdx),
        new FlightsLayer({
            id: "flights",
            data: activeFlightPaths,
            visible: true,
            updateTriggers: {
                getFilterValue: [showFlightPathsLayerByFlightId, previewFlightPathsLayerByFlightId],
            },
            filterMaps: {
                byFlight: showFlightPathsLayerByFlightId,
                byPreview: previewFlightPathsLayerByFlightId,
            },
            pdfSnapshotOpen,
            imageColorfinderOpen: videoMetadata.length > 0,
            activeImageIds,
            hoveredImageId,
            pickable: !someGeometryToolIsInUse,
        }),
        new VideoLayer({
            id: "video",
            data: videoMetadata,
            flightId,
            visible: true,
            distance: 75,
            crosshairEnabled,
            frameEnabled,
            flightPathEnabled,
            droneEnabled: droneIconEnabled,
            loading: isLoading(flightId),
            frameNumber: imageColorFinderSelectedFrame,
            mediaType: videoLayerMediaType,
            getVideoElement: () =>
                document.getElementById("video-plugin-player") as HTMLVideoElement | null,
        }),
    ]

    // https://github.com/visgl/deck.gl/issues/8450
    // deckGL can handle attaching a TerrainExtension to a layer, but fails on the inverse operation
    // This logic essentially "empties" deckGL's memory by memotarily passing an empty array
    // TODO: deckGL should fix this one day, so keep checking new releases

    const renderedLayers = terrainMapIsLoaded ? layers : []
    return <MapElements layers={renderedLayers} tooltipGenerator={layerSpecificTooltipGenerator} />
}
